import { CropYear } from "../../cropYear"
import { FieldViewModel, ICropping } from "../../fields"
import { arrayMin, createIndex, isArrayEmpty } from "../../utils/array"
import { isDate } from "../../utils/utils"
import { getPerennialDatesInYear } from "../perennial"
import { Years } from "./types"
import { CroppingHistoryProvider } from "./history"
import { LayoutFunction } from "./types"

interface InputCropping {

    croppingId: number,
    cropping: ICropping,
    vm: FieldViewModel,

    startDate: Date,
    endDate: Date,

    group: number,
    rowIndex: number,
    rowSpan: number,


}

export function createTimelineFields(fields: FieldViewModel[], history: CroppingHistoryProvider, years: Years, layout: LayoutFunction): TlField[] {

    const filter = (c: ICropping) => (c.YearId >= years.first) && (c.YearId <= years.last)

    const minDate = years && years.first ? CropYear.getYearStart(years.first) : undefined
    const maxDate = years && years.last ? CropYear.getYearEnd(years.last) : undefined

    const tlfields = fields.map(f => {

        const input = f.field.Cropping.filter(filter)

        const inputIndex = createIndex(input, i => i.CroppingId)

        function getRelated(rel: (c: ICropping) => ICropping[]) {

            return (c: ICropping) => {

                // Note: the inclusion of CroppingId in the sort order is just here to try a keep together
                // crops that share some successors
                return (rel(c) || [])
                    .filter(r => !!inputIndex[r.CroppingId])
                    .map(r => ({
                        node: r,
                        next: history.getNext(r)
                    }))
                    .map(r => ({
                        ...r,
                        sortKey: arrayMin((r.next || []), i => i.CroppingId),
                        area: r.node.Area,
                    }))
                    .sort((a, b) => (a.sortKey - b.sortKey) || (b.area - b.area))
                    .map(a => a.node)

            }

        }

        const getNext = getRelated(history.getNext)
        const getPrevious = getRelated(history.getPrevious)

        const getSequence = (c: ICropping) => ({
            previous: getPrevious(c),
            next: getNext(c)
        })

        const nodes = layout(input, getNext, getPrevious)

        const cropping = nodes
            .map(i => {


                const c = i.node

                const vm = f.croppings.find(c => c.croppingId == i.node.CroppingId)

                const dates = getCroppingDates(vm, getSequence, minDate, maxDate)

                return vm
                    ? {

                        croppingId: c.CroppingId,
                        cropping: c,

                        vm,

                        ...dates,

                        group: i.group,
                        rowIndex: i.rowIndex,
                        rowSpan: i.rowSpan,

                    }
                    : null

            })
            .filter(i => !!i)

        const tl = new Timeline()

        const tlfield = tl.createField(f, cropping, { getNext, getPrevious })

        return tlfield

    })

    return tlfields

}

class Timeline {

    private index: {
        [key: number]: InputCropping
    }

    createField(field: FieldViewModel, cropping: InputCropping[], history: CroppingHistoryProvider): TlField {

        this.index = createIndex(cropping, c => c.croppingId)

        const tlfield = new TlField(field, cropping)

        let groupIndex = 0;

        const roots = cropping.filter(c => isArrayEmpty(history.getPrevious(c.cropping)))

        while (true) {

            groupIndex++;

            const group = roots.filter(c => c.group == groupIndex)
            if (group.length == 0) break

            tlfield.groups.push(this.createGroupItems(group, history, 0))

        }

        return tlfield;

    }

    private createGroupItems(croppings: InputCropping[], history: CroppingHistoryProvider, rank: number): TlCropping[] {

        return croppings.map(c => {

            const tlc = new TlCropping(c)

            tlc.rank = rank
            tlc.top = c.rowIndex
            tlc.height = c.rowSpan

            const next = history.getNext(c.cropping).map(nc => this.index[nc.CroppingId]).filter(vm => !!vm);

            tlc.nextItems = this.createGroupItems(next, history, rank + 1)

            return tlc

        });

    }


}

export class TlField {

    groups: TlCropping[][];

    constructor(public vm: FieldViewModel, public cropping: InputCropping[]) {
        this.groups = [];
    }

}

export class TlCropping {

    readonly startDate: Date
    readonly endDate: Date

    top: number
    height: number

    rank: number

    nextItems: TlCropping[]

    constructor(public readonly cropping: InputCropping, public readonly vm = cropping.vm) {
        this.startDate = cropping.startDate
        this.endDate = cropping.endDate
    }

}

function getCroppingDates(vm: FieldViewModel, getSequence: (c: ICropping) => { previous: ICropping[], next: ICropping[] }, minDate: Date, maxDate: Date) {

    const c = vm.cropping

    let startDate = c.SowingDate || c.StartDate
    let endDate = c.EndDate

    if (vm.perennial) {

        const seq = getSequence(vm.cropping)

        const dates = getPerennialDatesInYear(vm.cropping, seq)

        startDate = dates.start
        endDate = dates.end

    }

    return {
        startDate: isDate(minDate) && (minDate > startDate) ? minDate : startDate,
        endDate: isDate(maxDate) && (maxDate < endDate) ? maxDate : endDate
    }

}
